import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { products } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interests = ["General Enquiry", ...products.map((p) => p.name), "EPC / LSTK Project", "Site Fabrication", "Careers"];

const inputCls =
  "w-full border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#C5221F] focus:ring-1 focus:ring-[#C5221F] transition-colors duration-200";

const EnquiryForm = ({ defaultInterest = "General Enquiry" }) => {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", interest: defaultInterest, message: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const err = {};
    if (form.name.trim().length < 2) err.name = "Please enter your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Please enter a valid email";
    if (form.message.trim().length < 10) err.message = "Please describe your requirement (min. 10 characters)";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/enquiry`, form);
      toast.success("Enquiry submitted", { description: "Our team will get back to you shortly." });
      setForm({ name: "", company: "", email: "", phone: "", interest: defaultInterest, message: "" });
      setErrors({});
    } catch {
      toast.error("Submission failed", { description: "Please try again or email us at sales@grengg.com." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-5" data-testid="enquiry-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="enq-name" className="font-mono-tech text-[10px] font-bold uppercase tracking-widest text-slate-500 block mb-2">Full Name *</label>
          <input id="enq-name" data-testid="enquiry-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" />
          {errors.name && <p className="mt-1.5 text-xs text-[#C5221F]" data-testid="enquiry-name-error">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="enq-company" className="font-mono-tech text-[10px] font-bold uppercase tracking-widest text-slate-500 block mb-2">Company</label>
          <input id="enq-company" data-testid="enquiry-company-input" className={inputCls} value={form.company} onChange={set("company")} placeholder="Organisation" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="enq-email" className="font-mono-tech text-[10px] font-bold uppercase tracking-widest text-slate-500 block mb-2">Email *</label>
          <input id="enq-email" type="email" data-testid="enquiry-email-input" className={inputCls} value={form.email} onChange={set("email")} placeholder="name@company.com" />
          {errors.email && <p className="mt-1.5 text-xs text-[#C5221F]" data-testid="enquiry-email-error">{errors.email}</p>}
        </div>
        <div>
          <label htmlFor="enq-phone" className="font-mono-tech text-[10px] font-bold uppercase tracking-widest text-slate-500 block mb-2">Phone</label>
          <input id="enq-phone" data-testid="enquiry-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91 ..." />
        </div>
      </div>
      <div>
        <label htmlFor="enq-interest" className="font-mono-tech text-[10px] font-bold uppercase tracking-widest text-slate-500 block mb-2">Area of Interest</label>
        <select id="enq-interest" data-testid="enquiry-interest-select" className={inputCls} value={form.interest} onChange={set("interest")}>
          {(interests.includes(form.interest) ? interests : [...interests, form.interest]).map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor="enq-message" className="font-mono-tech text-[10px] font-bold uppercase tracking-widest text-slate-500 block mb-2">Requirement *</label>
        <textarea id="enq-message" rows={5} data-testid="enquiry-message-input" className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about your equipment, project or scope — dimensions, codes, timeline…" />
        {errors.message && <p className="mt-1.5 text-xs text-[#C5221F]" data-testid="enquiry-message-error">{errors.message}</p>}
      </div>
      <button
        type="submit"
        disabled={busy}
        data-testid="enquiry-submit-btn"
        className="group inline-flex items-center gap-3 bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white font-display font-semibold px-8 py-4 text-sm uppercase tracking-widest transition-colors duration-300"
      >
        {busy ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
        {busy ? "Submitting…" : "Submit Enquiry"}
      </button>
    </form>
  );
};

export default EnquiryForm;
